import { GameModel } from "src/models/game/game.model";
import { TaskModel } from "src/models/task/task.model";
import { TaskUpdateModel } from "src/socket/events/TaskUpdate/taskUpdate.model";
import {
  TimerChangeAction,
  TimerChangeModel,
} from "src/socket/events/TimerChange/timerChange.model";
import {
  UserChangeAction,
  UserChangeModel,
} from "src/socket/events/UserChange/userChange.model";
import {
  VoteChangeAction,
  VoteChangeModel,
} from "src/socket/events/VoteChange/voteChange.model";
import { VoteRevealModel } from "src/socket/events/VoteReveal/voteReveal.model";
import { LocalStorageKeys } from "../localstorage/keys";
import { GameStateModel } from "../models/game/gameState.model";
import {
  CreateGameResponse,
  JoinToGameResponse,
  SystemsResponse,
} from "../socket/socket.responses";

export const createGameStateUpdater = (
  current: GameStateModel,
  response: CreateGameResponse
): GameStateModel => {
  const { data } = response;
  const newState: GameStateModel = {
    votingSystems: current.votingSystems,
    votingFinished: false,
    user: data.user,
    game: data.game,
    token: data.token,
  };
  localStorage.setItem(LocalStorageKeys.gameTokenKey, data.token);
  return newState;
};

export const joinGameStateUpdater = (
  current: GameStateModel,
  response: JoinToGameResponse
): GameStateModel => {
  const { data } = response;
  const newState: GameStateModel = {
    ...current,
    user: data.user,
    game: data.game,
    token: data.token,
    voteReveal: data.game.voteReveal ?? current.voteReveal,
    timer: data.game.timer,
  };
  localStorage.setItem(LocalStorageKeys.gameTokenKey, data.token);
  return newState;
};

export const systemsStateUpdater = (
  current: GameStateModel,
  response: SystemsResponse
): GameStateModel => {
  const { data } = response;
  const newState: GameStateModel = {
    ...current,
    votingSystems: data.systems,
  };
  return newState;
};

export const tokenUpdater = (
  current: GameStateModel,
  data: { token: string | null }
): GameStateModel => {
  const { token } = data;
  return { ...current, token };
};

export const userChangeUpdater = (
  current: GameStateModel,
  data: UserChangeModel
): GameStateModel => {
  const { action, user } = data;
  const { JOIN, UPDATE, LEAVE, DISCONNECT, RECONNECT } = UserChangeAction;
  const newState = { ...current };

  if (!newState.game || !newState.game.users) {
    return current;
  }

  const index = newState.game?.users?.findIndex(u => u.id === user.id);

  const { users } = newState.game;

  switch (action) {
    case JOIN:
      if (index === -1) {
        users.push(user);
      }
      break;
    case UPDATE:
      if (index !== -1) {
        if (newState.user?.id === user.id) {
          newState.user = { ...user };
        }
        users[index] = { ...user };
      }
      break;
    case LEAVE:
      if (index !== -1) {
        newState.game.users.splice(index, 1);
      }
      break;
    case DISCONNECT:
      if (index !== -1) {
        users[index].isConnected = false;
      }
      break;
    case RECONNECT:
      if (index !== -1) {
        users[index].isConnected = true;
      }
      break;
    default:
      break;
  }

  newState.game.users = [...users];
  return newState;
};

export const voteChangeUpdater = (
  current: GameStateModel,
  data: VoteChangeModel
): GameStateModel => {
  const { SET, REMOVE } = VoteChangeAction;
  const { action, gameId, userId } = data;
  const newState = { ...current };

  if (!newState.game) {
    return current;
  }
  if (newState.user?.vote) {
    newState.user.vote = undefined;
  }
  const index = newState.game?.task?.votes?.findIndex(
    v => v.userId === userId && v.gameId === gameId
  );
  switch (action) {
    case SET:
      if (index === -1 && newState.game.task) {
        newState.game.task.votes = [...(newState.game.task.votes ?? []), data];
      }
      break;
    case REMOVE:
      if (index !== -1 && newState.game.task) {
        newState.game.task.votes = newState.game.task.votes?.filter(
          v => !(v.gameId === gameId && v.userId === userId)
        );
      }
      break;
    default:
      break;
  }
  return newState;
};

export const timerChangeUpdater = (
  current: GameStateModel,
  data: TimerChangeModel
): GameStateModel => {
  const { START, CANCEL, UPDATE, FINISH, RESET } = TimerChangeAction;
  const { action, timer } = data;
  const newState = { ...current };

  switch (action) {
    case START:
      newState.timer = { ...timer };
      break;
    case UPDATE:
      newState.timer = { ...timer };
      break;
    case RESET:
      newState.timer = { ...timer };
      break;
    case FINISH:
      newState.timer = { ...timer };
      break;
    case CANCEL:
      newState.timer = undefined;
      break;
    default:
      break;
  }

  return newState;
};

export const voteRevealUpdater = (
  current: GameStateModel,
  data: VoteRevealModel
): GameStateModel => {
  const newState = { ...current };
  newState.voteReveal = data;
  newState.votingFinished = true;
  return newState;
};

export const newVotingUpdater = (
  current: GameStateModel,
  data: GameModel
): GameStateModel => {
  const newState = { ...current };
  if (!newState.game) {
    return current;
  }

  newState.game.name = data.name;
  newState.game.showCards = data.showCards;
  newState.game.task = data.task;

  newState.voteReveal = undefined;
  newState.votingFinished = false;
  newState.timer = undefined;
  return newState;
};

export const gameSummaryUpdater = (
  current: GameStateModel,
  data: TaskModel[]
): GameStateModel => {
  const newState: GameStateModel = { votingFinished: true, token: null };
  newState.votingSystems = current.votingSystems;
  newState.game = current.game;
  newState.gameSummary = data;
  return newState;
};

export const taskUpdater = (
  current: GameStateModel,
  data: TaskUpdateModel
): GameStateModel => {
  const newState = { ...current };
  if (!newState.game) {
    return newState;
  }

  newState.game.task = data.tasks
    ? data.tasks.find(t => t.id === data.selectedTaskId)
    : current.game?.task;
  if (newState.game.task) {
    newState.game.task.votes = [];
  }

  if (
    newState.voteReveal &&
    data.selectedTaskId &&
    newState.game.task?.summary
  ) {
    newState.voteReveal.finalResult = newState.game.task.summary.finalResult;
  }

  if (newState.game) {
    newState.game.allTasks = data.tasks;
  }

  return newState;
};
